import {
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@devhop/ui";
import { Clock, Loader2 } from "lucide-react";
import { useState } from "react";
import type { BranchProductWithDetails } from "../../domain/types";
import {
  useAcknowledgeStockAlert,
  useOpenShift,
  usePrefetchProductConfig,
  useStockAlerts,
} from "../api/queries";
import { useCartSheet } from "../context/CartSheetContext";
import { usePOSContext } from "../context/POSContext";
import { CartFloatingButton } from "../ui/CartFloatingButton";
import { CartSheet } from "../ui/CartSheet";
import { OpenShiftDialog } from "../ui/OpenShiftDialog";
import { ProductConfigDialog } from "../ui/ProductConfigDialog";
import { ProductMenu } from "../ui/ProductMenu";
import { StockAlertBanner } from "../ui/StockAlertBanner";

export function POSPage() {
  const { selection } = usePOSContext();
  const branchId = selection?.branchId ?? null;
  const deviceId = selection?.deviceId ?? null;

  const { data: openShift, isLoading: shiftLoading } = useOpenShift(
    branchId,
    deviceId,
  );

  // Get stock alerts
  const { data: stockAlerts = [] } = useStockAlerts(branchId);
  const acknowledgeAlertMutation = useAcknowledgeStockAlert();

  const [selectedProduct, setSelectedProduct] =
    useState<BranchProductWithDetails | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isOpenShiftDialogOpen, setIsOpenShiftDialogOpen] = useState(false);
  const { isOpen, close, open: openCartSheet } = useCartSheet();
  const prefetchProductConfig = usePrefetchProductConfig(branchId);

  const handleCartSheetChange = (open: boolean) => {
    if (open) {
      openCartSheet();
    } else {
      close();
    }
  };

  const handleProductClick = (product: BranchProductWithDetails) => {
    prefetchProductConfig(product.id);
    setSelectedProduct(product);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedProduct(null);
  };

  // Show loading state while checking shift
  if (shiftLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
        </div>
      </div>
    );
  }

  // Show shift not open message if no shift is open
  if (!openShift && branchId && deviceId) {
    return (
      <>
        <div className="flex h-full items-center justify-center p-4">
          <div className="w-full max-w-md">
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Clock className="h-12 w-12 text-muted-foreground" />
                </EmptyMedia>
                <EmptyTitle>ກະລຸນາເປີດກະກ່ອນ</EmptyTitle>
                <EmptyDescription>
                  ທ່ານຕ້ອງເປີດກະກ່ອນຈຶ່ງສາມາດຂາຍສິນຄ້າໄດ້
                </EmptyDescription>
              </EmptyHeader>
              <div className="mt-6">
                <Button
                  onClick={() => setIsOpenShiftDialogOpen(true)}
                  className="w-full"
                  size="lg"
                >
                  ເປີດກະ
                </Button>
              </div>
            </Empty>
          </div>
        </div>
        <OpenShiftDialog
          open={isOpenShiftDialogOpen}
          onOpenChange={setIsOpenShiftDialogOpen}
          branchId={branchId}
          deviceId={deviceId}
          onSuccess={() => {
            setIsOpenShiftDialogOpen(false);
          }}
        />
      </>
    );
  }

  const handleAcknowledgeAlert = async (alertId: string) => {
    await acknowledgeAlertMutation.mutateAsync(alertId);
  };

  // Show products if shift is open
  return (
    <div className="flex h-full flex-col">
      <StockAlertBanner
        alerts={stockAlerts}
        onAcknowledge={handleAcknowledgeAlert}
      />
      <div className="min-h-0 flex-1">
        <ProductMenu onProductClick={handleProductClick} />
      </div>
      <CartFloatingButton />
      <CartSheet open={isOpen} onOpenChange={handleCartSheetChange} />
      <ProductConfigDialog
        product={selectedProduct}
        open={isDialogOpen}
        onClose={handleCloseDialog}
      />
    </div>
  );
}
